// Consent gating.
//
// The effective consent state is derived, in priority order, from:
//   1. An explicit decision — amb('consent', 'granted'|'denied'), or a CMP
//      signal (IAB TCF v2 / Google Consent Mode v2).
//   2. The DNT / GPC browser signals, when honoured.
//   3. The configured default ('denied' is safest; 'granted' for first-party
//      analytics where consent isn't required by jurisdiction).
//
// Two per-app options pulled from GET /v1/config tune the gate:
//   - implicitConsent: when true the 'pending' state counts as permission to
//     emit (opt-out analytics) rather than buffering. An explicit 'denied' —
//     or a honoured DNT/GPC signal — still blocks.
//   - honorDoNotTrack: when false the DNT / GPC signals are ignored.
//
// While the gate is closed collectors stay registered (so we don't lose
// buffered LCP entries) but the transport buffer is held back; when it opens
// the buffer is flushed.

const STATES = { granted: 'granted', denied: 'denied', pending: 'pending' };

let _explicit = null;          // 'granted'|'denied' explicit decision; null = undecided
let _default = STATES.pending; // configured fallback when nothing else decides
let _dntSignal = false;        // browser Do-Not-Track says "do not track"
let _gpcSignal = false;        // browser Global-Privacy-Control says "do not track"
let _honorDnt = true;          // honour the DNT signal
let _honorGpc = true;          // honour the GPC signal
let _implicitConsent = false;  // pull option: 'pending' counts as emit-allowed
let _listeners = [];
let _last = { state: STATES.pending, emit: false };

export function initConsent(cfg) {
  cfg = cfg || {};
  _explicit = null;
  _default = cfg.defaultState in STATES ? cfg.defaultState : STATES.pending;
  _honorDnt = cfg.honorDnt !== false;
  _honorGpc = cfg.honorGpc !== false;
  _implicitConsent = false;

  _gpcSignal = !!(navigator && navigator.globalPrivacyControl);
  const dnt = (navigator && (navigator.doNotTrack || navigator.msDoNotTrack)) || (window && window.doNotTrack);
  _dntSignal = dnt === '1' || dnt === 'yes';

  hookTcfApi();
  hookGoogleConsentMode();
  _last = snapshot();
  return effective();
}

// The effective consent state: an explicit decision wins, then a honoured
// DNT/GPC signal forces 'denied', else the configured default.
function effective() {
  if (_explicit) return _explicit;
  if ((_honorGpc && _gpcSignal) || (_honorDnt && _dntSignal)) return STATES.denied;
  return _default;
}

export function getConsent() { return effective(); }

// The transport flush gate. Emission is allowed when consent is granted, or —
// in implicit-consent mode — when it is merely pending. An explicit 'denied',
// or a honoured DNT/GPC signal, still blocks.
export function shouldEmit() {
  const s = effective();
  return s === STATES.granted || (_implicitConsent && s === STATES.pending);
}

// An explicit decision from amb('consent', ...) or a CMP. 'pending' clears the
// decision, handing the choice back to the DNT/GPC signals and the default.
export function setConsent(next) {
  if (!(next in STATES)) return;
  _explicit = next === STATES.pending ? null : next;
  broadcast();
}

// Applies the per-app consent options pulled from GET /v1/config. May reopen
// the gate (honorDoNotTrack:false lifts a DNT/GPC denial; implicitConsent:true
// releases a 'pending' buffer) or close it — broadcast() flushes accordingly.
export function applyConsentConfig(opts) {
  if (!opts) return;
  if (typeof opts.implicitConsent === 'boolean') _implicitConsent = opts.implicitConsent;
  if (typeof opts.honorDoNotTrack === 'boolean') {
    _honorDnt = opts.honorDoNotTrack;
    _honorGpc = opts.honorDoNotTrack;
  }
  broadcast();
}

export function onConsentChange(fn) {
  _listeners.push(fn);
  return () => { _listeners = _listeners.filter((x) => x !== fn); };
}

function snapshot() { return { state: effective(), emit: shouldEmit() }; }

// Notify listeners when the effective state — or merely whether emission is
// allowed — changes. The implicit-consent flag flips `emit` without moving the
// state off 'pending', so both are tracked.
function broadcast() {
  const next = snapshot();
  if (next.state === _last.state && next.emit === _last.emit) return;
  _last = next;
  for (const l of _listeners) { try { l(next.state); } catch (_) { /* silent */ } }
}

function hookTcfApi() {
  try {
    if (typeof window.__tcfapi !== 'function') return;
    window.__tcfapi('addEventListener', 2, function (tcData, success) {
      if (!success || !tcData) return;
      // Vendor + purposes: hosts typically configure this with their own
      // vendor id; here we use purpose-1 (storage & access) as the gating
      // condition, which covers RUM beacons in practice.
      const ok = tcData.gdprApplies === false ||
        (tcData.eventStatus !== 'cmpuishown' && tcData.purpose && tcData.purpose.consents && tcData.purpose.consents['1'] === true);
      setConsent(ok ? STATES.granted : STATES.denied);
    });
  } catch (_) { /* silent */ }
}

function hookGoogleConsentMode() {
  try {
    // Google Consent Mode v2 pushes updates through window.dataLayer.
    // We watch for 'analytics_storage' which is the closest semantic match.
    if (!Array.isArray(window.dataLayer)) return;
    const origPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args) {
      const r = origPush(...args);
      for (const a of args) {
        if (Array.isArray(a) && a[0] === 'consent' && a[2] && a[2].analytics_storage) {
          setConsent(a[2].analytics_storage === 'granted' ? STATES.granted : STATES.denied);
        } else if (a && typeof a === 'object' && a.event === 'consent_update' && a.analytics_storage) {
          setConsent(a.analytics_storage === 'granted' ? STATES.granted : STATES.denied);
        }
      }
      return r;
    };
  } catch (_) { /* silent */ }
}
